/**
 * useUserProfile Hook
 * Shared between web and mobile for fetching another user's public profile
 */

import { useCallback, useEffect, useState } from 'react';
import { ApiError, type HttpClient } from '../api/httpClient';
import type { SettledBetStatus } from '../betResult';
import type { PublicCreatorInfo } from '../creatorPayout';
import type { FollowerPermissions, FriendPermissions } from '../permissionHelpers';
import type { BetsType, TimePeriod } from './useLeaderboard';

// =============================================================================
// Types
// =============================================================================

export type { TimePeriod, BetsType };

/** What the server lets this viewer see; private parts arrive empty or null. */
export interface ProfileVisibility {
  propBreakdown: boolean;
  recentBets: boolean;
  financialDetails: boolean;
}

/** The full profile: the owner, or a viewer whose plan includes profiles. */
export interface FullUserProfileData {
  limited: false;
  visibility: ProfileVisibility;
  user: {
    id: string;
    displayName: string;
    /** Owner only. */
    email?: string;
    /** Owner only. */
    role?: string;
    isVerified: boolean;
    linkedSportsbooks: string[];
    createdAt: Date;
    followerCount: number;
    followingCount: number;
    followerPermissions?: FollowerPermissions | null;
    /** Owner only. */
    friendPermissions?: FriendPermissions | null;
    socialLinks?: Record<string, string> | null;
  };
  creatorInfo?: PublicCreatorInfo | null;
  isFollowing: boolean;
  overallStats: {
    roi: number;
    winRate: number;
    totalBets: number;
    wins: number;
    losses: number;
    pushes: number;
    /** Null unless financial details are visible. */
    totalProfit: number | null;
    totalUnits: number | null;
    currentStreak: number;
    longestWinStreak: number;
    longestLossStreak: number;
    avgOdds: number;
  };
  legStats: {
    totalLegs: number;
    legsHit: number;
    legsMissed: number;
    legsPush: number;
    legsDnp: number;
    legHitRate: number;
    oversHit: number;
    oversTotal: number;
    overHitRate: number;
    undersHit: number;
    undersTotal: number;
    underHitRate: number;
  };
  propBreakdown: Array<{
    propType: string;
    totalBets: number;
    winRate: number;
    roi: number;
  }>;
  legPropBreakdown: Array<{
    propType: string;
    totalLegs: number;
    hits: number;
    misses: number;
    pushes: number;
    hitRate: number;
  }>;
  recentBets: Array<{
    id: string;
    settledAt: Date;
    createdAt?: Date;
    outcome: SettledBetStatus;
    name: string | null;
    tier: string | null;
    totalOdds: string | null;
    finalOdds: string | null;
    stake: number | null;
    potentialPayout: number | null;
    actualPayout: number | null;
    sportsbook: string | null;
    legCount: number;
    aiReasoning?: string | null;
    legs: Array<{
      player: string;
      team: string | null;
      prop: string;
      marketKey: string;
      line: string | null;
      overUnder: string | null;
      odds: string | null;
      outcome: string;
    }>;
  }>;
}

/** The public profile logged-out and Free viewers get: identity and the headline record, units included. */
export interface LimitedUserProfileData {
  limited: true;
  user: Pick<
    FullUserProfileData['user'],
    'id' | 'displayName' | 'isVerified' | 'createdAt' | 'followerCount' | 'followingCount'
  >;
  creatorInfo: PublicCreatorInfo | null;
  overallStats: Omit<FullUserProfileData['overallStats'], 'totalProfit'>;
}

export type UserProfileData = FullUserProfileData | LimitedUserProfileData;

export interface UseUserProfileOptions {
  httpClient: HttpClient;
  userId?: string;
  displayName?: string;
  timePeriod?: TimePeriod;
  betsType?: BetsType;
}

export interface UseUserProfileResult {
  profile: UserProfileData | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

// =============================================================================
// Hook
// =============================================================================

export function useUserProfile(options: UseUserProfileOptions): UseUserProfileResult {
  const { httpClient, userId, displayName, timePeriod = '30d', betsType = 'all' } = options;

  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadProfile = useCallback(async () => {
    if (!userId && !displayName) {
      setError('User ID or display name is required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let resolvedUserId = userId;

      // If displayName provided, look up userId first
      if (!resolvedUserId && displayName) {
        const lookupData = await httpClient.get<{ id: string; displayName: string }>(
          `/api/users/by-display-name/${encodeURIComponent(displayName)}`,
        );
        resolvedUserId = lookupData.id;
      }

      const data = await httpClient.get<UserProfileData>(
        `/api/leaderboard/user/${resolvedUserId}`,
        { period: timePeriod, betsType },
      );

      setProfile(data);
    } catch (err) {
      const errorMsg = ApiError.getUserMessage(err, 'Failed to load user profile');
      setError(errorMsg);
      console.error('Failed to load user profile:', err);
    } finally {
      setLoading(false);
    }
  }, [httpClient, userId, displayName, timePeriod, betsType]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  return {
    profile,
    loading,
    error,
    reload: loadProfile,
  };
}
